import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import Landing from './routes/index'
import Login from './routes/auth/Login'
import MfaVerifyPage from './routes/auth/MfaVerify'
import AppLayout from './components/layout/AppLayout'
import WorkersPage from './routes/app/Workers'
import BulkUploadPage from './routes/app/BulkUpload'
import FichasListPage from './routes/app/FichasList'
import FichaNuevaPage from './routes/app/FichaNueva'
import FichaDetailPage from './routes/app/FichaDetail'
import SettingsUsersPage from './routes/app/SettingsUsers'
import ExportPage from './routes/app/Export'
import MfaSetupPage from './routes/app/MfaSetup'
import { WorkerDetailRedirect } from './routes/app/WorkerDetailRedirect'
import WorkerMagicLinkPage from './routes/worker/MagicLink'
import WorkerFormPage from './routes/worker/Form'
import { ProtectedRoute } from './features/auth/ProtectedRoute'
import { AppRedirect } from './features/auth/AppRedirect'

// F2: lazy para aislar Recharts + componentes pesados en chunk separado (VR-205).
// Reduce bundle inicial ~98KB gzipped (solo roles admin/medico acceden).
const DashboardPage = lazy(() => import('./routes/app/Dashboard'))
const WorkerReportPage = lazy(() => import('./routes/app/WorkerReport'))

// F3 D4: lazy PDFs upload + validate — solo cargan cuando digitador/medico entra.
const PdfsUploadPage = lazy(() => import('./routes/app/PdfsUpload'))
const PdfsValidatePage = lazy(() => import('./routes/app/PdfsValidate'))

// F5 D10-D12: panel admin notificaciones (cronómetros + alarmas).
const NotificationsPage = lazy(() => import('./routes/app/Notifications'))

// F6: rescate algorítmico tiempo real + admin scoring rules.
const RescatePage = lazy(() => import('./routes/app/Rescate'))
const ScoringRulesPage = lazy(() => import('./routes/app/admin/ScoringRules'))

// F7: admin API keys management (worker forms + API analista).
const ApiKeysAdminPage = lazy(() => import('./routes/app/admin/ApiKeys'))
// F7: bandeja de entrada formularios recibidos (auto-refresh).
const FormulariosPage = lazy(() => import('./routes/app/Formularios'))

// F7.5: Expediente unificado + Admin Organizations CRUD inline.
const ExpedientePage = lazy(() => import('./routes/app/Expediente'))
const OrganizationsAdminPage = lazy(() => import('./routes/app/admin/Organizations'))
const OperativosAdminPage = lazy(() => import('./routes/app/admin/Operativos'))

function LazyFallback() {
  return (
    <div
      className="flex min-h-[40vh] items-center justify-center text-slate-400"
      role="status"
      aria-live="polite"
    >
      <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden />
      <span className="sr-only">Cargando módulo…</span>
    </div>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/mfa-verify" element={<MfaVerifyPage />} />

      {/* F3 — Worker magic link landing (público, autenticado por token opaco) */}
      <Route path="/m/:token" element={<WorkerMagicLinkPage />} />
      {/* F7 — Worker form self-service (Epworth, encuesta-salud, etc.) — JWT magic-link en sessionStorage */}
      <Route path="/m/form" element={<WorkerFormPage />} />

      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AppRedirect />} />
        <Route
          path="workers"
          element={
            <ProtectedRoute requireNav="nav-trabajadores">
              <WorkersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="workers/bulk-upload"
          element={
            <ProtectedRoute allowRoles={['admin']}>
              <BulkUploadPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="fichas"
          element={
            <ProtectedRoute requireNav="nav-fichas">
              <FichasListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="fichas/nueva"
          element={
            <ProtectedRoute requireNav="nav-fichas">
              <FichaNuevaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="fichas/:id"
          element={
            <ProtectedRoute requireNav="nav-fichas">
              <FichaDetailPage />
            </ProtectedRoute>
          }
        />
        {/* F7.6 — /app/operativos eliminada (MINIMAL SURFACE: 0 acciones únicas, solapa Fichas).
            Backend /api/operativos sigue activo — solo se quita la página frontend. */}
        <Route
          path="settings/users"
          element={
            <ProtectedRoute requireNav="nav-settings">
              <SettingsUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="export"
          element={
            <ProtectedRoute requireNav="nav-export">
              <ExportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="dashboard"
          element={
            <ProtectedRoute requireNav="nav-dashboard" allowRoles={['admin', 'medico']}>
              <Suspense fallback={<LazyFallback />}>
                <DashboardPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="reports/worker/:id"
          element={
            <ProtectedRoute requireNav="nav-reports">
              <Suspense fallback={<LazyFallback />}>
                <WorkerReportPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="mfa-setup"
          element={
            <ProtectedRoute allowRoles={['admin']}>
              <MfaSetupPage />
            </ProtectedRoute>
          }
        />
        {/* F7.6 — /app/trabajadores/:id absorbido por Expediente patient-centric.
            Redirect preserva deep-links externos (correos, magic-links viejos). */}
        <Route
          path="trabajadores/:id"
          element={
            <ProtectedRoute requireNav="nav-expediente">
              <WorkerDetailRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="pdfs-upload"
          element={
            <ProtectedRoute allowRoles={['admin', 'medico', 'digitador']}>
              <Suspense fallback={<LazyFallback />}>
                <PdfsUploadPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="pdfs-validate/:pdfInboundId"
          element={
            <ProtectedRoute allowRoles={['admin', 'medico', 'digitador']}>
              <Suspense fallback={<LazyFallback />}>
                <PdfsValidatePage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="notifications"
          element={
            <ProtectedRoute requireNav="nav-notifications" allowRoles={['admin', 'recepcionista']}>
              <Suspense fallback={<LazyFallback />}>
                <NotificationsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="rescate"
          element={
            <ProtectedRoute requireNav="nav-rescue" allowRoles={['admin', 'medico']}>
              <Suspense fallback={<LazyFallback />}>
                <RescatePage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/scoring-rules"
          element={
            <ProtectedRoute requireNav="nav-scoring-rules" allowRoles={['admin']}>
              <Suspense fallback={<LazyFallback />}>
                <ScoringRulesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/api-keys"
          element={
            <ProtectedRoute allowRoles={['admin']}>
              <Suspense fallback={<LazyFallback />}>
                <ApiKeysAdminPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="formularios"
          element={
            <ProtectedRoute allowRoles={['admin', 'medico', 'tens']}>
              <Suspense fallback={<LazyFallback />}>
                <FormulariosPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* F7.5 — Expediente unificado patient-centric (ADR-033). */}
        <Route
          path="expediente"
          element={
            <ProtectedRoute requireNav="nav-expediente">
              <Suspense fallback={<LazyFallback />}>
                <ExpedientePage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* F7.5 — Admin Empresas CRUD inline. */}
        <Route
          path="admin/organizations"
          element={
            <ProtectedRoute requireNav="nav-admin-empresas" allowRoles={['admin']}>
              <Suspense fallback={<LazyFallback />}>
                <OrganizationsAdminPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Admin Operativos CRUD inline — crea operativos (eventos médicos grupales). */}
        <Route
          path="admin/operativos"
          element={
            <ProtectedRoute requireNav="nav-admin-operativos" allowRoles={['admin']}>
              <Suspense fallback={<LazyFallback />}>
                <OperativosAdminPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
      </Route>

      {/* F3 — alias /profile/security para mfa-setup (spec mfa-setup-flow) */}
      <Route
        path="/profile/security"
        element={
          <ProtectedRoute allowRoles={['admin']}>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<MfaSetupPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
